import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../config/supabase';
import { sendTelegramNotification, formatDateFR } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import CarGallery from '../../cars/components/CarGallery';
import { listImagesForAgency } from '../../cars/utils/carImages';

export default function PublicReservationPage({ settings, agencyId }) {
  const [cars, setCars] = useState([]);
  const [imagesByCar, setImagesByCar] = useState({});
  const [rentals, setRentals] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [cin, setCin] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [reservationId, setReservationId] = useState('');
  const [error, setError] = useState('');
  const [lang, setLang] = useState(window.currentLang || 'fr');
  const [agencySettings, setAgencySettings] = useState(settings);

  useEffect(() => {
    if (!agencyId) return;
    let cancelled = false;
    let carsChannel = null;
    let rentalsChannel = null;

    const _sbRowToDoc = (row) => {
      if (!row) return null;
      return { id: row.id, ...(row.data || {}), agency_id: row.agency_id };
    };

    const fetchAgencyData = async () => {
      if (cancelled) return;
      try {
        // 1. Load agency details via secure RPC
        const { data: regInfo } = await supabase
          .rpc('get_agency_public_info', { p_agency: agencyId });
        if (regInfo && !cancelled) {
          setAgencySettings(s => ({ 
            ...s, 
            companyName: regInfo.companyName || s.companyName,
            companyPhone: regInfo.companyPhone || s.companyPhone,
            companyAddress: regInfo.companyAddress || s.companyAddress,
            currency: regInfo.currency || s.currency
          }));
        }

        // 2. Load cars via secure RPC
        const { data: carsData } = await supabase
          .rpc('get_agency_public_cars', { p_agency: agencyId });
        if (carsData && !cancelled) {
          setCars(carsData);
          // Load the full image gallery for every car (additive; falls back to
          // car.photo when a car has no gallery rows).
          try {
            const map = await listImagesForAgency(agencyId, carsData.map(c => c.id));
            if (!cancelled) setImagesByCar(map || {});
          } catch (_) {}
        }

        // 3. Load availability via secure RPC (excludes sensitive fields)
        const { data: rentalsData } = await supabase
          .rpc('get_agency_availability', { p_agency: agencyId });
        if (rentalsData && !cancelled) {
          setRentals(rentalsData);
        }
      } catch (err) {
        console.error('Error fetching public reservation data:', err);
      }
    };

    (async () => {
      // Ensure anonymous session first for RLS access
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          await supabase.auth.signInAnonymously();
          await new Promise(r => setTimeout(r, 300));
        }
      } catch (_) {}

      await fetchAgencyData();

      // Setup real-time listener for cars
      carsChannel = supabase.channel(`rt_public_cars_${agencyId}_${Date.now()}`)
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'cars', 
          filter: `agency_id=eq.${agencyId}` 
        }, fetchAgencyData)
        .subscribe();

      // Setup real-time listener for rentals
      rentalsChannel = supabase.channel(`rt_public_rentals_${agencyId}_${Date.now()}`)
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'rentals', 
          filter: `agency_id=eq.${agencyId}` 
        }, fetchAgencyData)
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (carsChannel) supabase.removeChannel(carsChannel);
      if (rentalsChannel) supabase.removeChannel(rentalsChannel);
    };
  }, [agencyId]);

  const isAr = lang === 'ar';
  const today = new Date().toISOString().split('T')[0];

  // No agency ID = invalid link
  if (!agencyId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center p-8 bg-white rounded-2xl shadow-md max-w-sm w-full">
          <div className="text-5xl mb-4">🔗</div>
          <h2 className="font-bold text-gray-900 text-lg mb-2">Lien invalide</h2>
          <p className="text-gray-500 text-sm">Ce lien de réservation est incomplet. Contactez l'agence.</p>
        </div>
      </div>
    );
  }

  // Check if a car is available for the selected dates
  const isCarAvailable = (carId, start, end) => {
    if (!start || !end) return true;
    return !rentals.some(r => {
      if (r.carId !== carId) return false;
      if (['cancelled', 'completed'].includes(r.status)) return false;
      const rStart = r.startDate;
      const rEnd = r.endDate || '9999-12-31';
      return !(end < rStart || start > rEnd);
    });
  };

  const filteredCars = useMemo(() => {
    return cars.filter(c => c.status !== 'maintenance' && isCarAvailable(c.id, startDate, endDate));
  }, [cars, rentals, startDate, endDate]);

  const selectedCar = cars.find(c => c.id === selectedCarId);

  const totalDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const d1 = new Date(startDate);
    const d2 = new Date(endDate);
    return Math.max(1, Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)));
  }, [startDate, endDate]);

  const dailyRate = Number(selectedCar?.dailyRate ?? selectedCar?.dailyPrice ?? 0) || 0;
  const totalPrice = totalDays * dailyRate;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!selectedCarId) return setError(isAr ? '⚠️ اختر سيارة' : '⚠️ Choisissez une voiture');
    if (!startDate || !endDate) return setError(isAr ? '⚠️ اختر التواريخ' : '⚠️ Choisissez les dates');
    if (endDate < startDate) return setError(isAr ? '⚠️ تاريخ النهاية يجب أن يكون بعد البداية' : '⚠️ Date de fin doit être après date début');
    if (!name.trim()) return setError(isAr ? '⚠️ الاسم مطلوب' : '⚠️ Nom requis');
    if (!phone.trim()) return setError(isAr ? '⚠️ الهاتف مطلوب' : '⚠️ Téléphone requis');
    if (!cin.trim()) return setError(isAr ? '⚠️ CIN مطلوب' : '⚠️ CIN requise');

    // Verify car still available
    if (!isCarAvailable(selectedCarId, startDate, endDate)) {
      return setError(isAr ? '⚠️ هذه السيارة غير متاحة في هذه التواريخ' : "⚠️ Cette voiture n'est plus disponible");
    }

    setSubmitting(true);
    try {
      const car = cars.find(c => c.id === selectedCarId);
      const payload = {
        carId: selectedCarId,
        carName: car ? `${car.brand} ${car.model}` : '',
        carPlate: car?.plate || '',
        carDailyPrice: Number(car?.dailyRate ?? car?.dailyPrice ?? 0) || 0,
        startDate,
        endDate,
        totalDays,
        totalPrice,
        clientName: name.trim(),
        clientPhone: phone.trim(),
        clientCin: cin.trim(),
        clientEmail: email.trim(),
        notes: notes.trim(),
        status: 'pending', // pending | confirmed | refused | converted
        createdAt: new Date().toISOString(),
        source: 'public_link'
      };

      // Insert via secure SECURITY DEFINER RPC
      const { data: insertedId, error: insErr } = await supabase
        .rpc('create_public_reservation', { 
          p_agency: agencyId, 
          p_data: payload 
        });

      if (insErr) throw insErr;
      setReservationId(insertedId);
      setSuccess(true);

      // Send Telegram notification to admin
      try {
        await sendTelegramNotification(
          buildTelegramMessage('online_reservation', {
            carName: payload.carName, carPlate: payload.carPlate,
            name, phone, cin,
            startDate: formatDateFR(startDate), endDate: formatDateFR(endDate),
            totalDays, totalPrice, currency: agencySettings.currency || 'MAD', notes
          }, agencySettings?.telegramLang),
          'online_reservation'
        );
      } catch {}
    } catch (err) {
      setError('❌ ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-indigo-600 to-purple-800 font-sans" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
          <div className="text-7xl mb-4">🎉</div>
          <h1 className="text-2xl font-black text-green-600 mb-3">
            {isAr ? 'تم إرسال الحجز بنجاح!' : 'Réservation envoyée avec succès !'}
          </h1>
          <p className="text-sm text-gray-600 leading-relaxed mb-6 font-medium">
            {isAr 
              ? 'شكراً لك على طلب الحجز. سيتصل بك فريقنا في أقرب وقت ممكن لتأكيد التفاصيل.'
              : 'Merci pour votre demande de réservation. Notre équipe vous contactera dans les plus brefs délais pour confirmer les détails.'}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
            <div className="text-xs font-bold text-blue-800 mb-1 uppercase tracking-wider">
              {isAr ? 'رقم الحجز:' : 'ID de réservation :'}
            </div>
            <div className="font-mono text-lg font-black text-blue-900">
              {reservationId.slice(0, 8).toUpperCase()}
            </div>
          </div>
          {agencySettings.companyPhone && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col items-center">
              <span className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">
                {isAr ? 'هاتف الوكالة:' : "Téléphone de l'agence :"}
              </span>
              <a 
                href={`tel:${agencySettings.companyPhone}`}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold transition-all text-sm"
              >
                📞 {agencySettings.companyPhone}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 bg-gradient-to-br from-indigo-600 to-purple-800 font-sans" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="p-6 text-center border-b border-gray-100 relative">
          {/* Language Switcher */}
          <div className="absolute top-4 right-4 flex gap-1 z-10">
            <button
              onClick={() => { setLang('fr'); window.currentLang = 'fr'; }}
              className={`px-2 py-1 rounded text-xs font-bold transition ${lang === 'fr' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              FR
            </button>
            <button
              onClick={() => { setLang('ar'); window.currentLang = 'ar'; }}
              className={`px-2 py-1 rounded text-xs font-bold transition ${lang === 'ar' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              AR
            </button>
          </div>
          
          <div className="text-4xl mb-2">🚗</div>
          <h1 className="text-xl font-black text-gray-900 uppercase">
            {(agencySettings.companyName || 'Auto Services').toUpperCase()}
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            {isAr ? 'احجز سيارتك بسهولة' : 'Réservez votre voiture en ligne'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="p-6">
          {/* Dates */}
          <div className="flex gap-4 mb-4 flex-wrap">
            <div className="w-40">
              <label className="block text-xs font-bold text-gray-700 mb-1">
                {isAr ? '📅 تاريخ البداية' : '📅 Date début'}
              </label>
              <input
                type="date"
                value={startDate}
                min={today}
                onChange={e => setStartDate(e.target.value)}
                required
                className="w-full px-2.5 py-1.5 border-2 border-gray-200 rounded-xl text-xs outline-none focus:border-blue-500"
              />
            </div>
            <div className="w-40">
              <label className="block text-xs font-bold text-gray-700 mb-1">
                {isAr ? '📅 تاريخ النهاية' : '📅 Date fin'}
              </label>
              <input
                type="date"
                value={endDate}
                min={startDate || today}
                onChange={e => setEndDate(e.target.value)}
                required
                className="w-full px-2.5 py-1.5 border-2 border-gray-200 rounded-xl text-xs outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Car selection */}
          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-700 mb-2">
              🚗 {isAr ? `اختر سيارة (${filteredCars.length} متاحة)` : `Choisir voiture (${filteredCars.length} disponible(s))`}
            </label>
            {filteredCars.length === 0 && startDate && endDate ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center text-xs text-amber-800">
                😔 {isAr ? 'لا توجد سيارات متاحة في هذه التواريخ' : 'Aucune voiture disponible pour ces dates'}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1">
                {(filteredCars.length === 0 ? cars.filter(c => c.status !== 'maintenance') : filteredCars).map(car => {
                  const isSelected = selectedCarId === car.id;
                  return (
                    <label
                      key={car.id}
                      className={`flex flex-col border-2 rounded-2xl cursor-pointer transition overflow-hidden shadow-sm hover:border-blue-400 ${
                        isSelected ? 'border-blue-500 bg-blue-50/50 ring-2 ring-blue-100' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="car"
                        value={car.id}
                        checked={isSelected}
                        onChange={() => setSelectedCarId(car.id)}
                        className="hidden"
                      />
                      {(() => {
                        const cover = (imagesByCar[car.id] && imagesByCar[car.id][0]?.url) || car.photo;
                        const count = (imagesByCar[car.id] || []).length;
                        return cover ? (
                          <div className="relative">
                            <img src={cover} alt={`${car.brand} ${car.model}`} loading="lazy" className="w-full h-24 object-cover" />
                            {count > 1 && (
                              <span className="absolute bottom-1.5 right-1.5 rounded-full bg-black/55 px-1.5 py-0.5 text-[9px] font-bold text-white backdrop-blur">
                                📷 {count}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="w-full h-24 bg-gray-100 flex items-center justify-center text-3xl">🚗</div>
                        );
                      })()}
                      <div className="p-3">
                        <div className="font-bold text-xs text-gray-900 truncate">{car.brand} {car.model}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{car.year || ''} • {car.fuel || ''}</div>
                        <div className="font-black text-emerald-600 text-sm mt-2">
                          {car.dailyRate || car.dailyPrice || 0} {agencySettings.currency || 'MAD'}
                          <span className="text-[9px] font-normal text-gray-400"> /j</span>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="bg-blue-500 text-white text-center text-[10px] font-bold py-1">
                          ✓ {isAr ? 'مختارة' : 'Sélectionnée'}
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected car — full image gallery (browse before reserving) */}
          {selectedCar && (
            <div className="mb-4">
              <div className="mb-2 text-xs font-bold text-gray-700">
                🖼️ {isAr ? 'صور السيارة' : 'Photos du véhicule'}
              </div>
              <CarGallery
                images={imagesByCar[selectedCar.id] || []}
                fallback={selectedCar.photo}
                alt={`${selectedCar.brand} ${selectedCar.model}`}
                isAr={isAr}
              />
            </div>
          )}

          {/* Summary */}
          {selectedCar && startDate && endDate && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl mb-4 flex justify-between items-center text-xs">
              <div className="text-green-800">
                <div className="font-bold">⏱️ {totalDays} {isAr ? 'يوم' : 'jour(s)'}</div>
                <div className="text-[10px] opacity-75">{formatDateFR(startDate)} → {formatDateFR(endDate)}</div>
              </div>
              <div className="text-right">
                <div className="text-green-700">{isAr ? 'المجموع' : 'Total'}</div>
                <div className="text-lg font-black text-green-600">{totalPrice} {agencySettings.currency || 'MAD'}</div>
              </div>
            </div>
          )}

          {/* Client info */}
          <div className="space-y-3 mb-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                👤 {isAr ? 'الاسم الكامل' : 'Nom complet'}
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                placeholder={isAr ? 'محمد العلوي' : 'Mohammed Alaoui'}
                className="w-full p-2 border border-gray-300 rounded-xl text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  📞 {isAr ? 'الهاتف' : 'Téléphone'}
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  required
                  placeholder="+212 6XX XX XX XX"
                  className="w-full p-2 border border-gray-300 rounded-xl text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  🆔 {isAr ? 'CIN' : 'CIN'}
                </label>
                <input
                  type="text"
                  value={cin}
                  onChange={e => setCin(e.target.value)}
                  required
                  placeholder="AB123456"
                  className="w-full p-2 border border-gray-300 rounded-xl text-sm font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                ✉️ {isAr ? 'البريد الإلكتروني (اختياري)' : 'Email (optionnel)'}
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="email@exemple.com"
                className="w-full p-2 border border-gray-300 rounded-xl text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                📝 {isAr ? 'ملاحظات (اختياري)' : 'Notes (optionnel)'}
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows="2"
                placeholder={isAr ? 'أي معلومات إضافية...' : 'Informations supplémentaires...'}
                className="w-full p-2 border border-gray-300 rounded-xl text-sm"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 mb-4">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className={`w-full py-3 rounded-xl font-bold text-white transition shadow-lg ${
              submitting 
                ? 'bg-gray-400 cursor-wait' 
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
            }`}
          >
            {submitting 
              ? (isAr ? '⏳ جارٍ الإرسال...' : 'Envoi...') 
              : (isAr ? '✅ إرسال الطلب' : 'Envoyer la réservation')}
          </button>
          
          <p className="text-center text-[10px] text-gray-400 mt-3 italic">
            {isAr ? '⏰ سيتم التواصل معك خلال 24 ساعة' : '⏰ Nous vous contacterons sous 24h'}
          </p>
        </form>

        {/* Footer */}
        <div className="bg-gray-50 p-4 border-t border-gray-100 text-center text-xs text-gray-500">
          <span>{agencySettings.companyName || 'Auto Services'}</span>
          {agencySettings.companyPhone && <span className="mx-2">• 📞 {agencySettings.companyPhone}</span>}
          {agencySettings.companyAddress && <span className="mx-2">• 📍 {agencySettings.companyAddress}</span>}
        </div>
      </div>
    </div>
  );
}
